// packages/web-ui/src/AppShell/HelpPopover.tsx
//
// ActivityBar "?" button. One-click opens the full user-guide modal
// (`<UserDocsModal>`); also responds to F1 (via `HELP_OPEN_EVENT` from
// `useDestinationKeybindings`). Previously this rendered a popover menu
// (Open docs / Keyboard shortcuts / Start tutorial / About) — those
// affordances now live in the modal's footer so the docs are reachable
// in a single click.
//
// The component keeps its file name + exported symbol + the
// `help-popover-trigger` testid so ActivityBar wiring, ActivityBar.test,
// and the TutorialTour step 4 anchor all continue to resolve.

import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { HelpCircle } from 'lucide-react';
import { HELP_OPEN_EVENT } from './useDestinationKeybindings.js';
import { OPEN_DOCS_EVENT } from '../components/welcome/UserDocsModal.js';

export interface HelpPopoverProps {
  /** Test-only handle: dispatch `OPEN_DOCS_EVENT` immediately on mount. */
  defaultOpen?: boolean;
}

function openDocs(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_DOCS_EVENT));
}

export function HelpPopover({ defaultOpen = false }: HelpPopoverProps): JSX.Element {
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    function onOpenEvent(): void {
      openDocs();
    }
    window.addEventListener(HELP_OPEN_EVENT, onOpenEvent);
    return () => {
      window.removeEventListener(HELP_OPEN_EVENT, onOpenEvent);
    };
  }, []);

  useEffect(() => {
    if (defaultOpen) openDocs();
  }, [defaultOpen]);

  return (
    <button
      ref={triggerRef}
      type="button"
      data-testid="help-popover-trigger"
      aria-label="Open user guide"
      aria-haspopup="dialog"
      title="User guide (F1)"
      onClick={openDocs}
      className="text-audi-red hover:bg-cream-100 focus-visible:ring-taupe-700 relative flex h-9 w-9 items-center justify-center rounded outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    >
      <HelpCircle size={20} aria-hidden="true" />
    </button>
  );
}
